// Module 1, Data & sets: discontinuity data in, stereonet, density, joint sets,
// Terzaghi weighting, rosette. GPL-3.0-or-later.
import { on as onStore } from "../../state/store.js";
import type { ModuleApi, ModuleInstance } from "../types.js";
import { X, reset, clearHooks, update, draw, on, onRender } from "./ctx.js";
import { layoutHTML } from "./layout.js";
import { mountNet, resize, poleCountChanged, setTool, cancelDraft } from "./net.js";
import { mountDensity } from "./density.js";
import { mountPanels } from "./panels.js";
import { mountSets, renderSets } from "./sets.js";
import { mountTable } from "./table.js";
import { mountTraverse } from "./traverse.js";
import { mountImporter } from "./importer.js";
import { mountDisplay } from "./display.js";
import { mountRose } from "./rose.js";
import { currentSVG } from "./exportsvg.js";

const LEFT_KEY = "dfsuite.leftHidden", RIGHT_KEY = "dfsuite.rightHidden";
const pref = (k: string) => { try { return localStorage.getItem(k) === "1"; } catch { return false; } };
const setPref = (k: string, v: boolean) => { try { localStorage.setItem(k, v ? "1" : "0"); } catch { /* ignore */ } };

export const dataModule: ModuleApi = {
  mount(root: HTMLElement): ModuleInstance {
    const ac = new AbortController();
    clearHooks(); reset(ac.signal);
    root.innerHTML = layoutHTML();
    const layout = root.querySelector(".layout")!;
    const setHidden = (side: "left" | "right", h: boolean) => {
      layout.classList.toggle(side + "-hidden", h); setPref(side === "left" ? LEFT_KEY : RIGHT_KEY, h);
      document.getElementById("toggle-" + side)?.setAttribute("aria-expanded", String(!h));
      requestAnimationFrame(() => { resize(); moveTabInk(); });
    };
    if (pref(LEFT_KEY)) layout.classList.add("left-hidden");
    if (pref(RIGHT_KEY)) layout.classList.add("right-hidden");

    mountNet(renderSets);
    mountDensity();
    mountPanels();
    mountSets();
    mountTable();
    mountTraverse();
    mountImporter((h) => setHidden("left", h));
    mountDisplay();
    mountRose();
    onRender((table) => { if (table) poleCountChanged(); });

    // tabs with the sliding underline
    const ink = root.querySelector<HTMLElement>(".tab-ink")!;
    function moveTabInk() {
      const b = root.querySelector<HTMLElement>('.tabs [data-tab][aria-selected="true"]');
      if (!b || !b.offsetParent) return;
      ink.style.left = b.offsetLeft + "px"; ink.style.width = b.offsetWidth + "px";
    }
    root.querySelectorAll<HTMLButtonElement>(".tabs [data-tab]").forEach((b) => on(b, "click", () => {
      X.activeTab = b.dataset.tab!;
      root.querySelectorAll<HTMLButtonElement>(".tabs [data-tab]").forEach((x) => x.setAttribute("aria-selected", String(x === b)));
      root.querySelectorAll<HTMLElement>(".tabpanel").forEach((pn) => (pn.hidden = pn.dataset.panel !== b.dataset.tab));
      moveTabInk();
    }));
    const ro = new ResizeObserver(() => requestAnimationFrame(moveTabInk)); ro.observe(root.querySelector(".tabs")!);
    on(window, "resize", moveTabInk);

    on(document, "keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape") { cancelDraft(); setTool("view"); draw(); }
    });
    const offs = [onStore("load", () => { X.selectedSet = -1; X.tablePage = 0; update(); }), onStore("restore", () => update())];

    let presentPrev: [boolean, boolean] | null = null;
    update();
    requestAnimationFrame(moveTabInk);
    return {
      unmount() { ac.abort(); ro.disconnect(); offs.forEach((f) => f()); clearHooks(); root.innerHTML = ""; },
      exportSVG: currentSVG,
      togglePanel(side) { const h = !layout.classList.contains(side + "-hidden"); setHidden(side, h); return !h; },
      panels: () => ({ left: !layout.classList.contains("left-hidden"), right: !layout.classList.contains("right-hidden") }),
      present(onP) {
        if (onP && !presentPrev) { presentPrev = [layout.classList.contains("left-hidden"), layout.classList.contains("right-hidden")]; setHidden("left", true); setHidden("right", true); }
        if (!onP && presentPrev) { setHidden("left", presentPrev[0]); setHidden("right", presentPrev[1]); presentPrev = null; }
        requestAnimationFrame(resize);
      },
    };
  },
};
